import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { ONEC_CONTRACT_VERSION, writeCommandBody, type WriteCommandIdentity } from '../../../shared/api/writeCommand'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { VatApplyResult, VatPreview, VatSummary } from '../model/types'
import { ONEC_VAT_SCHEMAS, decodeVatApply, decodeVatPreview, decodeVatSummary } from './contracts'
import { ONEC_VAT_URLS } from './urls'

/** Тег ставки НДС базы: запись пачкой перечитывает сводку этой базы. */
export const ONEC_VAT_TAG = 'OneCVat' as const

export interface OneCVatSummaryArgs {
  resourceId: string
}

export interface OneCVatPreviewArgs {
  resourceId: string
  expectedConfigurationVersion: string
  rateCode: string
  groupKey: string | null
}

export interface OneCVatApplyArgs {
  resourceId: string
  identity: WriteCommandIdentity
  rateCode: string
  refKeys: readonly string[]
}

const vatTag = (resourceId: string) => resourceTag(ONEC_VAT_TAG, normalizeRid(resourceId), 'summary')

/**
 * Endpoints ставки НДС товаров 1С в общем `baseApi`. Предпросмотр — читающий POST (без operationId и ключа: он ничего не
 * пишет); запись пачкой — пишущая команда, ключ и operationId приходят от вызывающего и переиспользуются при повторе той же пачки.
 */
export const vatApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_VAT_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecVatSummary: build.query<OneCEnvelope<VatSummary>, OneCVatSummaryArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_VAT_URLS.summary(resourceId),
        decode: onecEnvelopeDecoder(resourceId, decodeVatSummary),
      }),
      providesTags: (_result, _error, { resourceId }) => [vatTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    onecVatPreview: build.mutation<OneCEnvelope<VatPreview>, OneCVatPreviewArgs>({
      query: ({ resourceId, expectedConfigurationVersion, rateCode, groupKey }) => ({
        url: ONEC_VAT_URLS.preview(resourceId),
        method: 'POST',
        body: {
          contractVersion: ONEC_CONTRACT_VERSION,
          schemaVersion: ONEC_VAT_SCHEMAS.preview,
          expectedConfigurationVersion,
          data: { rateCode, groupKey },
        },
        decode: onecEnvelopeDecoder(resourceId, decodeVatPreview),
      }),
    }),
    onecVatApply: build.mutation<OneCEnvelope<VatApplyResult>, OneCVatApplyArgs>({
      query: ({ resourceId, identity, rateCode, refKeys }) => ({
        url: ONEC_VAT_URLS.apply(resourceId),
        method: 'POST',
        body: writeCommandBody(ONEC_VAT_SCHEMAS.apply, identity, { rateCode, refKeys }),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeVatApply),
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [vatTag(resourceId)],
    }),
  }),
})

export const { onecVatSummary, onecVatPreview, onecVatApply } = vatApi.endpoints
